import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  alpha,
} from '@mui/material';
import {
  ShowChart as ChartIcon,
  TrendingUp as UpIcon,
  TrendingDown as DownIcon,
} from '@mui/icons-material';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
} from 'recharts';
import api from '../../services/api';

const MacroScoreTrend = ({ currentScore }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(90);
  const [trendData, setTrendData] = useState([]);
  const [stats, setStats] = useState({
    trend: 'up',
    change: 0,
    changePercent: 0,
    volatility: 0,
  });

  const fetchTrendData = useCallback(async () => {
    try {
      const response = await api.get(`/macro/history?days=${period}`);
      const data = response.data || [];

      // Calculate statistics
      if (data.length > 1) {
        const first = data[0];
        const last = data[data.length - 1];
        const change = last.net_score - first.net_score;
        const changePercent = (change / Math.abs(first.net_score)) * 100;
        
        // Calculate volatility (standard deviation of daily changes)
        const changes = [];
        for (let i = 1; i < data.length; i++) {
          changes.push(data[i].net_score - data[i - 1].net_score);
        }
        const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;
        const variance = changes.reduce((sum, c) => sum + Math.pow(c - avgChange, 2), 0) / changes.length;
        const volatility = Math.sqrt(variance);

        setStats({
          trend: change > 0 ? 'up' : 'down',
          change,
          changePercent,
          volatility,
        });
      }

      setTrendData(data);
    } catch (error) {
      console.error('Error fetching trend data:', error);
    }
  }, [period]);

  useEffect(() => {
    fetchTrendData();
  }, [fetchTrendData]);

  const handlePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'white',
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            {data.date}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#667eea' }}>
            MOS: {data.mos != null ? data.mos.toFixed(2) : 'N/A'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#ff6b6b' }}>
            MRS: {data.mrs != null ? data.mrs.toFixed(2) : 'N/A'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#11998e' }}>
            Net: {data.net_score != null ? data.net_score.toFixed(2) : 'N/A'}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper', boxShadow: 'none' }, transition: 'none' }}>
      <CardContent sx={{ p: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ChartIcon sx={{ fontSize: 24, mr: 1.5, color: '#667eea' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {t('dashboard.macroTrend.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {t('dashboard.macroTrend.subtitle')}
              </Typography>
            </Box>
          </Box>

          {/* Period Selector */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={period}
              exclusive
              onChange={handlePeriodChange}
              size="small"
            >
              <ToggleButton value={30}>30D</ToggleButton>
              <ToggleButton value={60}>60D</ToggleButton>
              <ToggleButton value={90}>90D</ToggleButton>
            </ToggleButtonGroup>

            {/* Trend Indicator */}
            <Chip
              icon={stats.trend === 'up' ? <UpIcon /> : <DownIcon />}
              label={`${stats.change > 0 ? '+' : ''}${stats.change.toFixed(2)} (${stats.changePercent.toFixed(1)}%)`}
              color={stats.trend === 'up' ? 'success' : 'error'}
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>

        {/* Stats Row */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <Box sx={{ flex: 1, p: 1.5, bgcolor: alpha('#667eea', 0.05), borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {t('dashboard.macroTrend.currentScore')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
              {currentScore?.net_score?.toFixed(2) || 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.5, bgcolor: alpha('#ffa726', 0.05), borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {t('dashboard.macroTrend.volatility')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffa726' }}>
              ±{stats.volatility.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.5, bgcolor: alpha('#11998e', 0.05), borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {t('dashboard.macroTrend.periodChange')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: stats.change > 0 ? '#11998e' : '#ff6b6b' }}>
              {stats.change > 0 ? '+' : ''}{stats.change.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Chart */}
        <ResponsiveContainer width="100%" height={280} minWidth={400} minHeight={280}>
          <ComposedChart data={trendData}>
            <defs>
              <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#11998e" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#11998e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="date"
              stroke="#666"
              style={{ fontSize: '0.75rem' }}
              tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            />
            <YAxis stroke="#666" style={{ fontSize: '0.75rem' }} />
            <Tooltip content={<CustomTooltip />} cursor={false} wrapperStyle={{ zIndex: 1000 }} animationDuration={50} animationEasing="ease-out" />
            <Legend wrapperStyle={{ fontSize: '0.875rem' }} />

            {/* Reference Lines for Regime Thresholds */}
            <ReferenceLine y={10} stroke="#11998e" strokeDasharray="5 5" label="Risk-On" />
            <ReferenceLine y={-10} stroke="#ff6b6b" strokeDasharray="5 5" label="Risk-Off" />
            <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />

            {/* Net Score Area */}
            <Area
              type="monotone"
              dataKey="net_score"
              stroke="#11998e"
              strokeWidth={0}
              fill="url(#colorNet)"
              fillOpacity={1}
              activeDot={false}
            />

            {/* Lines */}
            <Line
              type="monotone"
              dataKey="mos"
              stroke="#667eea"
              strokeWidth={2}
              dot={false}
              name="MOS"
              activeDot={false}
            />
            <Line
              type="monotone"
              dataKey="mrs"
              stroke="#ff6b6b"
              strokeWidth={2}
              dot={false}
              name="MRS"
              activeDot={false}
            />
            <Line
              type="monotone"
              dataKey="net_score"
              stroke="#11998e"
              strokeWidth={3}
              dot={{ r: 3, fill: '#11998e', strokeWidth: 2, stroke: '#fff' }}
              name="Net Score"
              activeDot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default MacroScoreTrend;

